import { useMemo, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CHANNELS, CHANNEL_LABEL, CREATABLE_TYPES, IMPACT_LABEL, LEVELS, PRIORITY_LABEL, TICKET_TYPE_LABEL,
  URGENCY_LABEL, TICKET_TYPE_HELP, priorityFor, type Channel, type Level, type TicketType
} from '@veta-desk/domain';
import { Shell } from '../components/Shell';
import { PriorityBadge } from '../components/Badges';
import { area, btnPrimary, btnSecondary, field } from '../components/Modal';
import { AgentOptions } from '../components/AgentOptions';
import { api, ApiError, type InitiativeOption, type Meta } from '../lib/api';
import { useSession } from '../lib/session';

export default function NewTicket() {
  const { t } = useTranslation();
  const { me } = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const meta = useQuery({ queryKey: ['meta'], queryFn: () => api<Meta>('/meta'), staleTime: 5 * 60_000 });

  const [type, setType] = useState<TicketType>('INC');
  const [organizationId, setOrganizationId] = useState('');
  const [initiativeId, setInitiativeId] = useState('');
  const [requesterName, setRequesterName] = useState('');
  const [requesterEmail, setRequesterEmail] = useState('');
  const [channel, setChannel] = useState<Channel>('email');
  const [categoryId, setCategoryId] = useState('');
  const [impact, setImpact] = useState<Level>('medium');
  const [urgency, setUrgency] = useState<Level>('medium');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [assigneeId, setAssigneeId] = useState('');
  const [groupId, setGroupId] = useState('');
  const [error, setError] = useState('');

  const initiatives = useQuery({
    queryKey: ['initiatives', organizationId],
    queryFn: () => api<InitiativeOption[]>(`/organizations/${organizationId}/initiatives`),
    enabled: Boolean(organizationId),
    staleTime: 5 * 60_000
  });

  const categoryGroups = useMemo(() => {
    const groups = new Map<string, Meta['categories']>();
    for (const c of meta.data?.categories ?? []) groups.set(c.service, [...(groups.get(c.service) ?? []), c]);
    return [...groups.entries()];
  }, [meta.data]);

  const create = useMutation({
    mutationFn: () => api<{ number: string }>('/tickets', {
      method: 'POST',
      body: JSON.stringify({
        type, organizationId, initiativeId: initiativeId || null, requesterName, requesterEmail, channel,
        categoryId: categoryId || null, impact, urgency, subject, description, assigneeId: assigneeId || null, groupId: groupId || null
      })
    }),
    onSuccess: ({ number }) => {
      void queryClient.invalidateQueries({ queryKey: ['tickets'] });
      navigate(`/tickets/${number}`, { state: { created: true } });
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : t('errors.generic'))
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setError('');
    create.mutate();
  };

  const priority = priorityFor(impact, urgency);
  const label = 'mb-1.5 block text-sm font-medium';

  return (
    <Shell>
      <form onSubmit={submit} className="mx-auto flex max-w-4xl flex-col gap-5">
        <div>
          <h1 className="text-2xl font-semibold">{t('newTicket.title')}</h1>
          <p className="mt-1 text-sm text-muted">{t('newTicket.subtitle')}</p>
        </div>

        <fieldset className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <legend className="sr-only">{t('newTicket.type')}</legend>
          {CREATABLE_TYPES.map(ty => (
            <label key={ty} className={`flex cursor-pointer flex-col gap-1 rounded-lg border bg-white p-4 ${
              type === ty ? 'border-accent ring-1 ring-accent' : 'border-line'}`}>
              <span className="flex items-center gap-2 font-semibold">
                <input type="radio" name="type" checked={type === ty} onChange={() => setType(ty)} />
                {TICKET_TYPE_LABEL[ty]} <span className="font-mono text-xs text-muted">{ty}</span>
              </span>
              <span className="text-xs text-muted">{TICKET_TYPE_HELP[ty]}</span>
            </label>
          ))}
        </fieldset>

        <section className="grid grid-cols-1 gap-4 rounded-lg border border-line bg-white p-5 md:grid-cols-2">
          <div>
            <label htmlFor="org" className={label}>{t('newTicket.client')}</label>
            <select id="org" required className={field} value={organizationId}
              onChange={e => { setOrganizationId(e.target.value); setInitiativeId(''); }}>
              <option value="">{t('newTicket.chooseClient')}</option>
              {meta.data?.organizations.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="ini" className={label}>{t('newTicket.initiative')}</label>
            <select id="ini" className={field} value={initiativeId} disabled={!organizationId}
              onChange={e => setInitiativeId(e.target.value)}>
              <option value="">{organizationId ? t('newTicket.noInitiative') : t('newTicket.chooseClientFirst')}</option>
              {initiatives.data?.map(i => (
                <option key={i.id} value={i.id}>{i.code} · {i.name}{i.kind === 'evolutive' ? ' (evolutivo)' : ''}</option>
              ))}
            </select>
            <p className="mt-1 text-xs text-muted">{t('newTicket.initiativeHelp')}</p>
          </div>
          <div>
            <label htmlFor="rq-name" className={label}>{t('newTicket.requester')}</label>
            <input id="rq-name" required className={field} value={requesterName} onChange={e => setRequesterName(e.target.value)}
              placeholder={t('newTicket.requesterPlaceholder')} autoComplete="off" />
          </div>
          <div>
            <label htmlFor="rq-email" className={label}>{t('newTicket.requesterEmail')}</label>
            <input id="rq-email" type="email" className={field} value={requesterEmail} onChange={e => setRequesterEmail(e.target.value)}
              placeholder="nombre@cliente.com" autoComplete="off" />
          </div>
          <div>
            <label htmlFor="channel" className={label}>{t('newTicket.channel')}</label>
            <select id="channel" className={field} value={channel} onChange={e => setChannel(e.target.value as Channel)}>
              {CHANNELS.filter(c => c !== 'portal').map(c => <option key={c} value={c}>{CHANNEL_LABEL[c]}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="cat" className={label}>{t('newTicket.category')}</label>
            <select id="cat" className={field} value={categoryId} onChange={e => setCategoryId(e.target.value)}>
              <option value="">{t('newTicket.noCategory')}</option>
              {categoryGroups.map(([service, cats]) => (
                <optgroup key={service} label={service}>
                  {cats.map(c => <option key={c.id} value={c.id}>{c.module}</option>)}
                </optgroup>
              ))}
            </select>
          </div>
        </section>

        <section className="grid grid-cols-1 gap-4 rounded-lg border border-line bg-white p-5 md:grid-cols-[1fr_1fr_auto]">
          <div>
            <label htmlFor="impact" className={label}>{t('newTicket.impact')}</label>
            <select id="impact" className={field} value={impact} onChange={e => setImpact(e.target.value as Level)}>
              {LEVELS.map(l => <option key={l} value={l}>{IMPACT_LABEL[l]}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="urgency" className={label}>{t('newTicket.urgency')}</label>
            <select id="urgency" className={field} value={urgency} onChange={e => setUrgency(e.target.value as Level)}>
              {LEVELS.map(l => <option key={l} value={l}>{URGENCY_LABEL[l]}</option>)}
            </select>
          </div>
          <div className="flex flex-col justify-center">
            <span className={label}>{t('newTicket.priority')}</span>
            <span className="flex h-11 items-center gap-2 text-sm" aria-live="polite">
              <PriorityBadge priority={priority} /> {PRIORITY_LABEL[priority].split('·')[1]}
            </span>
          </div>
        </section>

        <section className="flex flex-col gap-4 rounded-lg border border-line bg-white p-5">
          <div>
            <label htmlFor="subject" className={label}>{t('newTicket.subject')}</label>
            <input id="subject" required minLength={5} maxLength={200} className={field} value={subject}
              onChange={e => setSubject(e.target.value)} />
          </div>
          <div>
            <label htmlFor="desc" className={label}>{t('newTicket.description')}</label>
            <textarea id="desc" required minLength={10} rows={7} className={area} value={description}
              onChange={e => setDescription(e.target.value)} placeholder={t('newTicket.descriptionPlaceholder')} />
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[200px] flex-1">
              <label htmlFor="group" className={label}>{t('newTicket.group')}</label>
              <select id="group" className={field} value={groupId} onChange={e => setGroupId(e.target.value)}>
                <option value="">{t('newTicket.noGroup')}</option>
                {meta.data?.groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
            </div>
            <div className="min-w-[200px] flex-1">
              <label htmlFor="assignee" className={label}>{t('newTicket.assignee')}</label>
              <select id="assignee" className={field} value={assigneeId} onChange={e => setAssigneeId(e.target.value)}>
                <option value="">{t('newTicket.leaveUnassigned')}</option>
                <AgentOptions meta={meta.data} groupId={groupId} />
              </select>
            </div>
            {me && assigneeId !== me.uid && (
              <button type="button" className={btnSecondary} onClick={() => setAssigneeId(me.uid)}>{t('newTicket.assignToMe')}</button>
            )}
          </div>
        </section>

        {error && <p role="alert" className="text-sm font-medium text-[#8F1D1D]">{error}</p>}

        <div className="flex gap-3">
          <button type="submit" className={btnPrimary} disabled={create.isPending}>
            {create.isPending ? t('newTicket.creating') : t('newTicket.create', { type: TICKET_TYPE_LABEL[type].toLowerCase() })}
          </button>
          <button type="button" className={btnSecondary} onClick={() => navigate(-1)}>{t('common.cancel')}</button>
        </div>
      </form>
    </Shell>
  );
}
